import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Download, PlayCircle, RefreshCw, ShieldOff, Archive, CheckCircle2, XCircle, Clock, HelpCircle, RotateCcw } from "lucide-react";
import { hasPerm } from "@/lib/roles";
import { RetentionLog } from "@/components/RetentionLog";
import { BackupHistoryFilters, SortableDateHeader } from "@/components/backup/BackupHistoryFilters";
import { useBackupHistoryFilter } from "@/components/backup/useBackupHistoryFilter";

// Página de Backup do tenant — mostra apenas backups da empresa ativa.
// Servidor gera 1 zip diário por empresa às 03:00 BRT (configurável pelo SA).
// Cliente pode disparar backup manual da própria empresa via "Gerar backup agora".
const fmtSize = (b) => !b ? "—" : b < 1024*1024 ? `${(b/1024).toFixed(1)} KB` : `${(b/1024/1024).toFixed(2)} MB`;
const fmtDate = (i) => { try { return new Date(i).toLocaleString("pt-BR"); } catch { return i; } };

const STATUS_META = {
  success: { label: "Sucesso", color: "bg-emerald-100 text-emerald-800 border-emerald-300", icon: CheckCircle2 },
  error:   { label: "Erro",    color: "bg-red-100 text-red-800 border-red-300",             icon: XCircle },
  running: { label: "Rodando", color: "bg-blue-100 text-blue-800 border-blue-300",           icon: Clock },
  unknown: { label: "Legado",  color: "bg-slate-100 text-slate-700 border-slate-300",        icon: HelpCircle },
};

export default function Backup() {
  const { user, currentEmpresaId } = useAuth();
  const canBackup = hasPerm(user, "backup", "v");
  const canRetry = hasPerm(user, "backup", "e");   // iter 91: retry usa mesmo gate do delete
  const [files, setFiles] = useState([]);
  const hist = useBackupHistoryFilter(files);
  const rows = hist.filtered;
  const [loading, setLoading] = useState(false);
  const [running, setRunning] = useState(false);
  const [retrying, setRetrying] = useState(null);   // iter 91: filename em retry
  const [nextRun, setNextRun] = useState(null);   // {next_run_local, human}

  const retryBackup = async (f) => {
    if (retrying) return;
    setRetrying(f.filename);
    try {
      const r = await api.post(`/backups/${encodeURIComponent(f.filename)}/retry`);
      const size = ((r.data.size_bytes || 0) / 1024).toFixed(1);
      toast.success(`Backup reprocessado: ${size} KB`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Retry falhou");
    } finally { setRetrying(null); }
  };

  const load = async () => {
    setLoading(true);
    try {
      const [rf, rn] = await Promise.all([
        api.get("/backups"),
        api.get("/backups/next-run"),
      ]);
      setFiles(rf.data || []);
      setNextRun(rn.data);
    }
    catch (e) { toast.error(e?.response?.data?.detail || "Erro ao carregar"); }
    finally { setLoading(false); }
  };
  useEffect(() => { if (canBackup) load(); }, [canBackup, currentEmpresaId]);

  const runNow = async () => {
    setRunning(true);
    try {
      const r = await api.post("/backups/run-now");
      toast.success(`Backup criado: ${r.data.filename || "OK"}`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Falha"); }
    finally { setRunning(false); }
  };

  const download = async (filename) => {
    try {
      const base = process.env.REACT_APP_BACKEND_URL || "";
      const resp = await fetch(`${base}/api/backups/${encodeURIComponent(filename)}/download`, {
        credentials: "include",
        headers: currentEmpresaId ? { "X-Empresa-Id": currentEmpresaId } : {},
      });
      if (!resp.ok) throw new Error("Falha");
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href = url; a.download = filename;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
    } catch (e) {
      console.error("Falha no download do backup", filename, e);
      toast.error(e?.message ? `Erro no download: ${e.message}` : "Erro no download");
    }
  };

  if (!canBackup) return (
    <div className="max-w-xl mx-auto mt-16 text-center" data-testid="page-backup-denied">
      <ShieldOff className="w-16 h-16 mx-auto text-rose-500 mb-4"/>
      <h1 className="text-2xl font-bold">Sem permissão</h1>
      <p className="text-muted-foreground mt-2">Peça ao administrador a permissão "Backup/Restore".</p>
    </div>
  );

  return (
    <div className="space-y-6" data-testid="page-backup">
      <div className="flex items-center gap-3 flex-wrap">
        <div className="p-3 rounded-xl bg-emerald-600 text-white"><Archive className="w-6 h-6" /></div>
        <div className="flex-1 min-w-[200px]">
          <h1 className="text-3xl font-extrabold leading-tight">Backup de Dados</h1>
          <p className="text-sm text-muted-foreground">
            Backup automático diário da <b>sua empresa</b>. Os arquivos contêm apenas dados vinculados à sua conta.
            <span className="ml-1 inline-flex items-center gap-1 text-xs bg-emerald-50 text-emerald-800 border border-emerald-200 rounded px-1.5 py-0.5 align-middle">
              🔒 Isolamento por empresa
            </span>
          </p>
        </div>
        <Button onClick={load} disabled={loading} variant="outline" data-testid="btn-refresh">
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`} />Atualizar
        </Button>
        <Button onClick={runNow} disabled={running}
          className="bg-gradient-to-r from-emerald-600 to-emerald-700 hover:from-emerald-700 hover:to-emerald-800 shadow-md h-11 px-5 font-semibold"
          data-testid="btn-run-backup">
          <PlayCircle className={`w-5 h-5 mr-2 ${running ? "animate-pulse" : ""}`} />
          {running ? "Gerando backup…" : "Gerar Backup Agora"}
        </Button>
      </div>

      {nextRun && nextRun.configured && (
        <Card className="border-sky-200 bg-gradient-to-br from-sky-50 to-white" data-testid="card-next-backup">
          <CardContent className="pt-4 pb-4 flex items-center gap-3">
            <div className="p-2 rounded-lg bg-sky-100">
              <Clock className="w-5 h-5 text-sky-700" />
            </div>
            <div>
              <div className="text-xs uppercase tracking-wide text-sky-600 font-semibold">
                Próximo backup automático{nextRun.mode === "global" && " · horário global"}
              </div>
              <div className="text-lg font-bold text-slate-800" data-testid="next-backup-time">{nextRun.next_run_local}</div>
              <div className="text-xs text-muted-foreground">em <b>{nextRun.human}</b> · horários: <b>{nextRun.configured_slots}</b> (BRT)</div>
              {nextRun.mode === "global" && (
                <div className="text-xs text-amber-700 mt-1" data-testid="next-backup-global-hint">
                  Esta empresa não tem horário próprio — usa o horário global. Personalize em <b>Backup Automático</b> nas Configurações da Empresa.
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}
      {nextRun && !nextRun.configured && (
        <Card className="border-amber-200 bg-amber-50" data-testid="card-next-backup-nao-configurado">
          <CardContent className="pt-4 pb-4 flex items-center gap-3">
            <div className="p-2 rounded-lg bg-amber-100">
              <Clock className="w-5 h-5 text-amber-700" />
            </div>
            <div>
              <div className="text-xs uppercase tracking-wide text-amber-700 font-semibold">Próximo backup indisponível</div>
              <div className="text-sm font-semibold text-slate-800">{nextRun.message || "Não foi possível determinar o próximo backup automático."}</div>
            </div>
          </CardContent>
        </Card>
      )}

      {running && (
        <Card className="border-emerald-300 bg-emerald-50/50">
          <CardContent className="pt-5 pb-5">
            <div className="flex items-center gap-3 mb-3">
              <PlayCircle className="w-5 h-5 text-emerald-600 animate-pulse" />
              <div className="text-sm font-semibold text-emerald-900">Zipando seus dados — aguarde…</div>
            </div>
            <div className="w-full h-2 bg-emerald-100 rounded-full overflow-hidden">
              <div className="h-full w-1/3 bg-gradient-to-r from-emerald-500 to-emerald-600 rounded-full" style={{ animation: "backup-slide 1.4s ease-in-out infinite" }} />
            </div>
            <style>{`@keyframes backup-slide { 0%{transform:translateX(-100%);width:30%} 50%{width:60%} 100%{transform:translateX(400%);width:30%} }`}</style>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader className="space-y-3"><CardTitle className="text-base">Arquivos disponíveis</CardTitle>
          <BackupHistoryFilters f={hist} total={files.length} shown={rows.length} />
        </CardHeader>
        <CardContent className="p-0 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-y">
              <tr className="text-left">
                <th className="px-4 py-2 font-semibold">Arquivo</th>
                <SortableDateHeader sortDir={hist.sortDir} onToggle={hist.toggleSort} />
                <th className="px-4 py-2 font-semibold">Tamanho</th>
                <th className="px-4 py-2 font-semibold">Status</th>
                <th className="px-4 py-2 font-semibold text-right">Ação</th>
              </tr>
            </thead>
            <tbody>
              {files.length === 0 && (
                <tr><td colSpan={5} className="text-center py-10 text-muted-foreground" data-testid="backups-empty">
                  <Archive className="w-10 h-10 mx-auto mb-2 text-slate-400" />
                  Nenhum backup da sua empresa ainda. Clique em "Gerar Backup Agora".
                </td></tr>
              )}
              {rows.length === 0 && files.length > 0 && (
                <tr><td colSpan={5} className="px-4 py-8 text-center text-muted-foreground text-sm" data-testid="hist-empty-filtered">Nenhum backup no filtro selecionado.</td></tr>
              )}
              {rows.map((f) => {
                const meta = STATUS_META[f.status] || STATUS_META.unknown;
                const Icon = meta.icon;
                return (
                  <tr key={f.filename} className="border-b hover:bg-slate-50/50" data-testid={`bkp-${f.filename}`}>
                    <td className="px-4 py-3 font-mono text-xs">{f.filename}</td>
                    <td className="px-4 py-3 text-xs whitespace-nowrap">{fmtDate(f.modified_at)}</td>
                    <td className="px-4 py-3 text-xs">{fmtSize(f.size_bytes)}</td>
                    <td className="px-4 py-3">
                      <Badge className={`text-[10px] uppercase ${meta.color}`}>
                        <Icon className="w-3 h-3 mr-1" />{meta.label}
                      </Badge>
                      {f.error && <div className="text-[10px] text-rose-600 mt-1 truncate max-w-[220px]" title={f.error}>{f.error}</div>}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex gap-1 justify-end">
                        <Button size="sm" variant="outline" onClick={() => download(f.filename)}
                          disabled={f.status === "error" || !f.size_bytes}
                          className="h-8" data-testid={`btn-dl-${f.filename}`}>
                          <Download className="w-3.5 h-3.5 mr-1" />Baixar
                        </Button>
                        {canRetry && f.status === "error" && (
                          <Button size="sm" variant="outline" onClick={() => retryBackup(f)}
                            disabled={retrying === f.filename}
                            className="h-8 border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                            data-testid={`btn-retry-${f.filename}`}>
                            <RotateCcw className={`w-3.5 h-3.5 mr-1 ${retrying === f.filename ? "animate-spin" : ""}`} />
                            {retrying === f.filename ? "…" : "Retry"}
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </CardContent>
      </Card>

      <RetentionLog />
    </div>
  );
}
